import React from 'react';
import { Skeleton, Table, Paper } from '@mantine/core';

/**
 * TableSkeleton Props
 */
export interface TableSkeletonProps {
  /**
   * Number of skeleton rows to display
   * @default 5
   */
  rows?: number;

  /**
   * Number of columns to display
   * @default 5
   */
  columns?: number;

  /**
   * Whether to show table header skeleton
   * @default true
   */
  showHeader?: boolean;

  /**
   * Whether to wrap in Paper component
   * @default true
   */
  withPaper?: boolean;
}

/**
 * TableSkeleton - Generic loading placeholder for data tables
 *
 * Features:
 * - Configurable rows and columns
 * - Optional header skeleton
 * - Optional Paper wrapper
 * - Responsive skeleton sizes
 * - Animates while loading
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 5,
  showHeader = true,
  withPaper = true
}): React.ReactElement => {
  const tableContent = (
    <Table highlightOnHover>
      {/* Table Header Skeleton */}
      {showHeader && (
        <Table.Thead>
          <Table.Tr>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Table.Th key={colIndex}>
                <Skeleton height={16} width={`${60 + (colIndex % 3) * 10}%`} />
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
      )}

      {/* Table Body Skeleton */}
      <Table.Tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Table.Tr key={rowIndex}>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Table.Td key={colIndex}>
                <Skeleton
                  height={14}
                  width={`${50 + ((rowIndex + colIndex) % 4) * 15}%`}
                />
              </Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );

  if (withPaper) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        {tableContent}
      </Paper>
    );
  }

  return tableContent;
};
